import React, { useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export const InstallmentPlanDialog = ({ open, candidate, onClose, onCreated }) => {
  const [numInstallments, setNumInstallments] = useState("3");
  const [intervalDays, setIntervalDays] = useState("30");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const total = Number(candidate?.payable_total || candidate?.total || 0);
  const n = parseInt(numInstallments, 10) || 0;
  const perRate = n >= 2 ? total / n : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (n < 2) {
      toast.error("Mindestens 2 Raten.");
      return;
    }
    setSaving(true);
    try {
      await apiClient.post(`/invoices/${candidate.invoice_id}/installment-plan`, {
        num_installments: n,
        interval_days: parseInt(intervalDays, 10) || 30,
        start_date: startDate,
      });
      toast.success(`Ratenzahlung vereinbart: ${n} Raten à ~CHF ${perRate.toFixed(2)}. Rechnung wird nicht mehr gemahnt.`);
      onClose();
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ratenzahlung fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="installment-plan-dialog">
        <DialogHeader>
          <DialogTitle>Ratenzahlung vereinbaren</DialogTitle>
          <DialogDescription>
            Rechnung <span className="font-medium">{candidate?.invoice_number}</span> · Offen:{" "}
            <span className="font-mono">{total.toFixed(2)} {candidate?.currency || "CHF"}</span>
            <br />
            Rechnungen mit aktiver Ratenzahlung werden nicht mehr gemahnt.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-3" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Anzahl Raten</Label>
              <Input type="number" min="2" max="24" value={numInstallments} onChange={(e) => setNumInstallments(e.target.value)} data-testid="installment-count-input" />
            </div>
            <div className="space-y-1">
              <Label>Intervall (Tage)</Label>
              <Input type="number" min="7" max="92" value={intervalDays} onChange={(e) => setIntervalDays(e.target.value)} data-testid="installment-interval-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Erste Rate fällig am</Label>
            <SwissDateField  value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="installment-start-date-input" />
          </div>
          {n >= 2 && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="installment-preview">
              {n} Raten à ca. <span className="font-mono font-semibold">CHF {perRate.toFixed(2)}</span>, alle {intervalDays} Tage.
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="installment-cancel">Abbrechen</Button>
            <Button type="submit" disabled={saving} data-testid="installment-submit">
              {saving ? "Speichert …" : "Ratenplan erstellen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
